import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const FORMSPREE_URL = 'https://formspree.io/f/xojnbeva';

function FeedbackModal() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [form, setForm] = useState({ category: 'bug', message: '', email: '' });

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => setStatus('idle'), 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          category: form.category,
          message: form.message,
          email: form.email || '(not provided)',
          _subject: `[Prepify] ${form.category}: ${form.message.slice(0, 60)}`,
        }),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ category: 'bug', message: '', email: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <>
      <button
        className="feedback-fab"
        onClick={() => setOpen(true)}
        title={t('feedback.button_title')}
        aria-label={t('feedback.button_title')}
      >
        💬
      </button>

      {open && (
        <div className="feedback-backdrop" onClick={handleClose}>
          <div
            className="feedback-modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-title"
          >
            <div className="feedback-header">
              <h3 id="feedback-title">{t('feedback.title')}</h3>
              <button className="feedback-close" onClick={handleClose} aria-label={t('feedback.close')}>×</button>
            </div>

            {status === 'success' ? (
              <div className="feedback-success">
                <span className="feedback-success-icon">✓</span>
                <p>{t('feedback.success')}</p>
                <button className="feedback-submit-btn" onClick={handleClose}>{t('feedback.close')}</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="feedback-form">
                <label className="feedback-label">
                  {t('feedback.category_label')}
                  <select
                    className="feedback-select"
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  >
                    <option value="bug">{t('feedback.cat_bug')}</option>
                    <option value="feature">{t('feedback.cat_feature')}</option>
                    <option value="other">{t('feedback.cat_other')}</option>
                  </select>
                </label>

                <label className="feedback-label">
                  {t('feedback.message_label')}
                  <textarea
                    className="feedback-textarea"
                    placeholder={t('feedback.message_placeholder')}
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    required
                    rows={4}
                  />
                </label>

                <label className="feedback-label">
                  {t('feedback.email_label')}
                  <input
                    className="feedback-input"
                    type="email"
                    placeholder={t('feedback.email_placeholder')}
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  />
                </label>

                {status === 'error' && (
                  <p className="feedback-error">{t('feedback.error')}</p>
                )}

                <button
                  className="feedback-submit-btn"
                  type="submit"
                  disabled={status === 'submitting' || !form.message.trim()}
                >
                  {status === 'submitting' ? t('feedback.submitting') : t('feedback.submit')}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default FeedbackModal;
